import { FC, useState } from 'react';
import { Asset, Creator, NoodleProductTypes, Price, Product, ProductType } from '@typings/graphql-models';
import EditButton from '@components/EditButton';
import {
  ArrowRight,
  Broadcast,
  ChatText,
  Lightning,
  BookOpen,
  Users,
  Cloud,
  Desktop,
  Package,
  ChatCenteredText,
  CalendarCheck,
  CreditCard,
  Tag,
  Stack,
  Icon,
} from '@phosphor-icons/react';
import CustomLink from '@components/CustomLink';
import Buttons from '@components/Buttons';
import classNames from 'classnames';
import s from './ServiceCard.module.scss';
import ProgressIndicator from '../ProgressIndicator';

type Props = {
  to: string;
  creator: Pick<Creator, 'name'> & {
    image?: Pick<Asset, 'url'> | null;
  };
  product: Pick<Product, 'title' | 'isActive' | 'description'> & {
    prices: Array<Pick<Price, 'price' | 'id' | 'isActive' | 'currency'>>;
    productTypes: Pick<ProductType, 'title' | 'noodleProductType'>[];
  };
  canEdit: boolean;
  isFeatured?: boolean;
  newTab?: boolean;
};

const productTypeIcons: Record<NoodleProductTypes, Icon> = {
  [NoodleProductTypes.Broadcast]: Broadcast,
  [NoodleProductTypes.Byte]: ChatText,
  [NoodleProductTypes.Charge]: Lightning,
  [NoodleProductTypes.Handbook]: BookOpen,
  [NoodleProductTypes.InPersonSession]: Users,
  [NoodleProductTypes.Lite]: Cloud,
  [NoodleProductTypes.Noodle]: Package,
  [NoodleProductTypes.NoodleSaaS]: Desktop,
  [NoodleProductTypes.OnlineSession]: Desktop,
  [NoodleProductTypes.ProductGroup]: Stack,
  [NoodleProductTypes.Response]: ChatCenteredText,
  [NoodleProductTypes.Subscription]: CreditCard,
  [NoodleProductTypes.Consultation]: CalendarCheck,
  [NoodleProductTypes.Collection]: Tag,
};

const pickLabel = (noodleProductType: NoodleProductTypes | null | undefined): string => {
  if (noodleProductType === NoodleProductTypes.Lite) {
    return 'Private Chat';
  }
  if (noodleProductType === NoodleProductTypes.OnlineSession) {
    return 'Remote';
  }
  if (noodleProductType === NoodleProductTypes.InPersonSession) {
    return 'In-Person';
  }
  return '';
};

const NewServiceCard: FC<Props> = ({ to, creator: _creator, product, canEdit, isFeatured, newTab }) => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <CustomLink
      to={to}
      onClick={() => setIsLoading(true)}
      className={classNames(s[`serviceCard${isFeatured ? 'Featured' : ''}`], s.disableHover)}
      newTab={newTab}
    >
      {isFeatured && <div className={s.serviceCardFeatured__label}>Featured</div>}
      {canEdit && (
        <div className={s.serviceCard__edit}>
          <EditButton isMock />
        </div>
      )}
      <div className={s.serviceCard__copy}>
        <strong className={s.serviceCard__copy__title} style={(!product.isActive && { color: 'var(--color-gray-75)' }) || undefined}>
          {product.title}
        </strong>

        {product.description && <p className={s.serviceCard__copy__description}>{product.description}</p>}

        <div className={s.serviceCard__tags}>
          {product.productTypes?.map((type, idx) => {
            if (!type.noodleProductType) return null;

            const IconComponent = productTypeIcons[type.noodleProductType];

            return (
              <div className={s.serviceCard__tags__tag} key={idx}>
                <IconComponent size={16} />
                <span className={s.productTypeTag__title}>{pickLabel(type.noodleProductType)}</span>
              </div>
            );
          })}
        </div>

        {!canEdit && !isFeatured && (
          <div className={s.serviceCard__seeAll}>
            <span>See All</span>

            {isLoading ? <ProgressIndicator color="var(--color-primary)" size={16} /> : <ArrowRight size={16} />}
          </div>
        )}
      </div>

      {!product.isActive && <small className={s.serviceCard__inactive}>Inactive</small>}
      {isFeatured && (
        <Buttons isThird isFullWidth className={s.serviceCardFeatured__cta}>
          Get it now
        </Buttons>
      )}
    </CustomLink>
  );
};

export default NewServiceCard;
